// survey/sequence/buildSequence.js
import {
  introductionTypeQuestion,
  categoryTypeQuestion,
  questionTypeQuestion,
  feedbackTypeQuestion,
  modularOutcomeTypeQuestion,
  followupAiTypeQuestion,
  submitTypeQuestion,
  nextQuestionBtn,
  introductionBtn,
  submitSurvey as SUBMIT_BTN,
} from "constants/questionTypes";

/**
 * A tiny state container we pass to each builder so they can mutate the
 * sequence and counters in a controlled way.
 */
function createBuilderState() {
  return {
    seq: {},
    resetResponsesOnChange: {},
    running_total: 0,
    total: 0,
    visualTotal: 0,
    counts: {
      CatLength: 0,
      feedbackLength: 0,
      commLength: 0,
      quesLength: 0,
    },
    aux: {
      prevResp: {},
      neverAskResp: {},
    },
  };
}

/* ---------------------------------- Intro --------------------------------- */
function buildIntroSequence(state) {
  const { seq } = state;
  let { running_total, total, visualTotal } = state;

  const pushIntro = (id, btnText) => {
    seq[id] = {
      id,
      sort_order: id,
      quesType: introductionTypeQuestion,
      buttonType: introductionBtn,
      intro_id: id,
      total: id,
      skip: false,
      buttonText: btnText,
      visualTotal,
      clicked: true,
      response: { response: true },
    };
  };

  running_total = 1; total = 1; pushIntro(1, "Start");
  running_total = 2; total = 2; pushIntro(2, "Next");
  running_total = 3; total = 3; pushIntro(3, "Next");
  running_total = 4; total = 4; pushIntro(4, "I agree");

  state.running_total = running_total;
  state.total = total;
  state.visualTotal = visualTotal;
}

/* ------------------------------- Categories -------------------------------- */
function buildCategorySequence(state, { categoryQues, employeeCategory, organizationName }) {
  if (!Array.isArray(categoryQues) || categoryQues.length === 0) return;

  const { seq, counts, aux } = state;
  let { running_total, total, visualTotal } = state;
  let catQuesNo = 0;

  categoryQues.forEach((q) => {
    if (q?.input === "always_ask") {
      running_total += 1; visualTotal += 1; counts.CatLength += 1; total += 1; catQuesNo += 1;
      seq[running_total] = {
        id: running_total,
        name: q.name,
        from: `always_ask ${q.id}`,
        category_question_id: q.id,
        quesType: categoryTypeQuestion,
        buttonType: nextQuestionBtn,
        skip: false,
        sno: running_total,
        catQuesNo,
        visualTotal,
        clicked: false,
        response: false,
        buttonText: "Next",
      };
      return;
    }

    if (q?.input === "ask_if_not_found") {
      const empCat = employeeCategory?.find(ec => ec?.name_id === q.id)?.value_id;
      running_total += 1; counts.CatLength += 1; catQuesNo += 1;

      if (empCat) {
        const responseId = q.options.find(rq => rq?.id === empCat)?.id;
        seq[running_total] = {
          id: running_total,
          name: q.name,
          from: `ask_if_not_found ${q.id}`,
          category_question_id: q.id,
          quesType: categoryTypeQuestion,
          buttonType: nextQuestionBtn,
          skip: true,
          sno: running_total,
          catQuesNo,
          visualTotal,
          clicked: false,
          category_id: q.id,
          response: { response: responseId },
          priority: q.priority,
          buttonText: "Next",
        };
        aux.prevResp[running_total] = { id: q.id, response: responseId, priority: q.priority };
      } else {
        visualTotal += 1; total += 1;
        seq[running_total] = {
          id: running_total,
          name: q.name,
          from: `ask_if_not_found  ${q.id}`,
          category_question_id: q.id,
          quesType: categoryTypeQuestion,
          buttonType: nextQuestionBtn,
          skip: false,
          sno: running_total,
          catQuesNo,
          visualTotal,
          clicked: false,
          response: false,
          buttonText: "Next",
        };
      }
      return;
    }

    if (q?.input === "never_ask") {
      const empCat = employeeCategory?.find(ec => ec?.name_id === q.id)?.value_id ?? null;
      running_total += 1; counts.CatLength += 1; catQuesNo += 1;
      seq[running_total] = {
        id: running_total,
        name: q.name,
        from: `never_ask  ${q?.id}`,
        category_question_id: q.id,
        quesType: categoryTypeQuestion,
        buttonType: nextQuestionBtn,
        skip: true,
        sno: running_total,
        catQuesNo,
        visualTotal,
        clicked: false,
        category_id: q.id,
        response: { response: empCat },
        priority: q.priority,
        buttonText: "Next",
      };
      aux.neverAskResp[running_total] = { id: q.id, response: empCat, priority: q.priority };
      return;
    }

    running_total += 1; counts.CatLength += 1; total += 1; visualTotal += 1; catQuesNo += 1;
    seq[running_total] = {
      id: running_total,
      name: q.name,
      from: `undefined`,
      category_question_id: q.id,
      quesType: categoryTypeQuestion,
      buttonType: nextQuestionBtn,
      skip: false,
      sno: running_total,
      catQuesNo,
      visualTotal,
      clicked: false,
      response: false,
      buttonText: "Next",
    };
  });

  running_total += 1; state.total += 1;
  const orgName = organizationName || "";
  seq[running_total] = {
    id: running_total,
    sort_order: running_total,
    quesType: categoryTypeQuestion,
    total: running_total,
    visualTotal,
    skip: false,
    buttonType: nextQuestionBtn,
    question: "Thank you for answering these preliminary questions.",
    description: `Now we are going to ask you some questions about your experience with ${orgName}. Your experience matters.`,
    clicked: true,
    response: { response: true },
    buttonText: "Next",
  };

  state.running_total = running_total;
  state.visualTotal = visualTotal;
}

/* ------------------------------ Survey Questions ----------------------------- */
/**
 * questionList is expected to be an array of question objects coming from your
 * serializer. We’ll store:
 * - question_text: from q.q (fallbacks: q.question, q.question_text)
 * - factor:       from q.factor (fallback: q.factor_index, or 0)
 * - ques_order:   running Likert order (1..N)
 */
function buildSurveyQuestionSequence(state, { questionList }) {
  if (!Array.isArray(questionList)) return;

  const { seq, counts } = state;
  let { running_total, total, visualTotal } = state;
  let quesListNo = 0;

  questionList.forEach((q) => {
    running_total += 1; quesListNo += 1;

    const questionText = q?.question ?? "(question not found)";
    const factor = (typeof q?.factor === "number" ? q.factor : undefined) ?? 0;

    counts.quesLength += 1; total += 1; visualTotal += 1;
    seq[running_total] = {
      id: running_total,
      quesType: questionTypeQuestion,
      ques_id: q.ques_id,
      buttonType: nextQuestionBtn,
      skip: false,
      sno: running_total,
      quesListNo,
      visualTotal,
      clicked: false,
      response: false,
      buttonText: "Next",
      factor,
      question_text: questionText,
    };
  });

  state.running_total = running_total;
  state.total = total;
  state.visualTotal = visualTotal;
}

/* --------------------------------- Outcomes -------------------------------- */
function buildOutcomeQuestionSequence(state, { outcomes }) {
  const groups = outcomes?.questions || [];
  if (!Array.isArray(groups) || groups.length === 0) return;

  const { seq } = state;
  let { running_total, total, visualTotal } = state;

  groups.forEach(group => {
    (group.questions || []).forEach(mq => {
      running_total += 1; total += 1; visualTotal += 1;
      seq[running_total] = {
        id: running_total,
        quesType: modularOutcomeTypeQuestion,
        buttonType: nextQuestionBtn,
        buttonText: "Next",
        skip: false,
        sno: running_total,
        visualTotal,
        clicked: false,
        response: false,
        name: group.name,
        q: group.id,
        s: mq.id,
        question: mq,
      };
    });
  });

  state.running_total = running_total;
  state.total = total;
  state.visualTotal = visualTotal;
}

/* -------------------------------- Follow-ups ------------------------------- */
function buildFollowupQuestionSequence(state, { min_followup, max_followup }) {
  const rows = Number(max_followup || 0) + Number(min_followup || 0);
  if (!rows) return;

  const { seq, counts } = state;
  let { running_total, total, visualTotal } = state;

  Array.from({ length: rows }).forEach(() => {
    visualTotal += 1; running_total += 1; total += 1; counts.feedbackLength += 1;
    seq[running_total] = {
      id: running_total,
      quesType: feedbackTypeQuestion,
      skip: false,
      sno: running_total,
      buttonType: nextQuestionBtn,
      visualTotal,
      feedListNo: counts.feedbackLength,
      clicked: false,
      response: false,
      buttonText: "Next",
    };

    const quesRefId = running_total;
    visualTotal += 1; running_total += 1; total += 1;
    seq[running_total] = {
      id: running_total,
      quesType: followupAiTypeQuestion,
      skip: false,
      sno: running_total,
      buttonType: nextQuestionBtn,
      visualTotal,
      feedListNo: counts.feedbackLength,
      clicked: false,
      response: false,
      quesRefId,
      buttonText: "Next",
    };
  });

  state.running_total = running_total;
  state.total = total;
  state.visualTotal = visualTotal;
}


/* ---------------------------------- Submit --------------------------------- */
function buildSubmitSequence(state) {
  const { seq } = state;
  let { running_total, total, visualTotal } = state;

  running_total += 1; total += 1;
  seq[running_total] = {
    id: running_total,
    quesType: submitTypeQuestion,
    buttonType: SUBMIT_BTN,
    buttonText: "Submit",
    skip: false,
    sno: running_total,
    visualTotal,
    clicked: false,
    response: { response: true },
  };

  state.running_total = running_total;
  state.total = total;
}

/* --------------------------------- Build Survey Question Sequence ---------------------------------- */

export function buildSequence({
  categoryQues = [],
  employeeCategory = [],
  questionList = [],
  organizationName = "",
  outcomes = null,
  min_followup = 0,
  max_followup = 0,
}) {
  const state = createBuilderState();

  // 1) intro pages (1..4)
  buildIntroSequence(state);

  // 2) categories + completion screen
  buildCategorySequence(state, { categoryQues, employeeCategory, organizationName });

  // 3) core questions (now include question_text/factor/ques_order)
  buildSurveyQuestionSequence(state, { questionList });

  // 4) outcomes (modular outcome questions)
  buildOutcomeQuestionSequence(state, { outcomes });

  // 5) follow-ups
  buildFollowupQuestionSequence(state, { min_followup, max_followup });

  // 6) submit
  buildSubmitSequence(state);

  const { seq, resetResponsesOnChange, running_total, visualTotal, total, counts, aux } = state;

  return {
    sequence: seq,
    meta: {
      total,
      introQuestion: 4,
      visualTotal,
      runningTotal: running_total,
      introduction: counts.quesLength,
      questions: counts.quesLength,
      category: counts.CatLength,
      comments: counts.commLength,
      feedback: counts.feedbackLength,
      resetResponsesOnChange,
    },
    prevFoundResponse: aux.prevResp,
    neverAskResponse: aux.neverAskResp,
  };
}
